import * as THREE from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { cubeSurface } from './cubeSurface';

export interface SceneInfo {
  backend: 'WebGPU' | 'WebGL2';
  calls: number;
  triangles: number;
  geometries: number;
  textures: number;
  gpuBytes: number;
}

export interface SceneHandle {
  update(positions: Float32Array, broken: Uint8Array): void;
  render(): void;
  resize(): void;
  setBeamsVisible(v: boolean): void;
  setNodesVisible(v: boolean): void;
  setEco(eco: boolean): void;
  info(): SceneInfo;
}

function gridTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  g.fillStyle = '#5b6168';
  g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 4000; i++) {
    const v = 80 + Math.random() * 40;
    g.fillStyle = `rgba(${v},${v},${v + 4},0.35)`;
    g.fillRect(Math.random() * 256, Math.random() * 256, 2, 2);
  }
  g.strokeStyle = 'rgba(255,255,255,0.18)';
  g.lineWidth = 2;
  g.strokeRect(1, 1, 254, 254);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(100, 100);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

export async function createScene(
  container: HTMLElement,
  nodeCount: number,
  beamPairs: Uint32Array,
  opts: { forceWebGL?: boolean } = {}
): Promise<SceneHandle> {
  const renderer = new THREE.WebGPURenderer({ antialias: true, forceWebGL: !!opts.forceWebGL });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.shadowMap.enabled = true;
  container.appendChild(renderer.domElement);
  await renderer.init();
  const backendObj = renderer.backend as unknown as { isWebGPUBackend?: boolean };
  const backend: 'WebGPU' | 'WebGL2' = backendObj.isWebGPUBackend ? 'WebGPU' : 'WebGL2';

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x9fb8d0);
  scene.fog = new THREE.Fog(0x9fb8d0, 40, 160);

  const camera = new THREE.PerspectiveCamera(50, container.clientWidth / container.clientHeight, 0.05, 500);
  camera.position.set(4.5, 3.2, 5.5);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0, 0.8, 0);
  controls.enableDamping = true;
  controls.maxPolarAngle = Math.PI * 0.495;
  controls.minDistance = 1.5;
  controls.maxDistance = 40;

  scene.add(new THREE.HemisphereLight(0xdbe8ff, 0x4a4436, 1.1));
  const sun = new THREE.DirectionalLight(0xfff1dc, 2.4);
  sun.position.set(6, 12, 4);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -8;
  sun.shadow.camera.right = 8;
  sun.shadow.camera.top = 8;
  sun.shadow.camera.bottom = -8;
  sun.shadow.bias = -0.0005;
  scene.add(sun, sun.target);

  const ground = new THREE.Mesh(new THREE.PlaneGeometry(400, 400), new THREE.MeshStandardMaterial({ map: gridTexture(), roughness: 0.9 }));
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  // suprafața cubului: fiecare vertex urmează un nod al fizicii
  const n = Math.round(Math.cbrt(nodeCount));
  const surf = cubeSurface(n);
  const vCount = surf.vertexNode.length;
  const surfGeo = new THREE.BufferGeometry();
  surfGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vCount * 3), 3).setUsage(THREE.DynamicDrawUsage));
  surfGeo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(vCount * 3), 3).setUsage(THREE.DynamicDrawUsage));
  surfGeo.setIndex(new THREE.BufferAttribute(surf.index, 1));
  const cube = new THREE.Mesh(surfGeo, new THREE.MeshStandardMaterial({ color: 0xd4552a, metalness: 0.35, roughness: 0.45 }));
  cube.castShadow = true;
  cube.receiveShadow = true;
  cube.frustumCulled = false;
  scene.add(cube);

  // grinzile
  const beamCount = beamPairs.length / 2;
  const beamGeo = new THREE.BufferGeometry();
  beamGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(beamCount * 6), 3).setUsage(THREE.DynamicDrawUsage));
  const beams = new THREE.LineSegments(beamGeo, new THREE.LineBasicMaterial({ color: 0x1b2633, transparent: true, opacity: 0.55 }));
  beams.frustumCulled = false;
  scene.add(beams);

  // nodurile
  const nodes = new THREE.InstancedMesh(new THREE.SphereGeometry(0.025, 8, 6), new THREE.MeshBasicMaterial({ color: 0xffd166 }), nodeCount);
  nodes.frustumCulled = false;
  nodes.visible = false;
  scene.add(nodes);
  const m4 = new THREE.Matrix4();

  const gpuBytes = (vCount * 6 + surf.index.length) * 4 + beamCount * 6 * 4 + nodeCount * 64 + 256 * 256 * 4;
  let eco = false;

  const center = new THREE.Vector3();
  const delta = new THREE.Vector3();

  function update(pos: Float32Array, broken: Uint8Array): void {
    // camera orbitală urmărește centrul cubului, păstrând unghiul ales de jucător
    center.set(0, 0, 0);
    for (let i = 0; i < nodeCount; i++) center.x += pos[i * 3], center.y += pos[i * 3 + 1], center.z += pos[i * 3 + 2];
    center.multiplyScalar(1 / nodeCount);
    delta.copy(center).sub(controls.target).multiplyScalar(0.08);
    controls.target.add(delta);
    camera.position.add(delta);
    sun.position.set(controls.target.x + 6, 12, controls.target.z + 4);
    sun.target.position.copy(controls.target);
    const sp = surfGeo.attributes.position.array as Float32Array;
    for (let v = 0; v < vCount; v++) {
      const k = surf.vertexNode[v] * 3;
      sp[v * 3] = pos[k];
      sp[v * 3 + 1] = pos[k + 1];
      sp[v * 3 + 2] = pos[k + 2];
    }
    surfGeo.attributes.position.needsUpdate = true;
    surfGeo.computeVertexNormals();
    if (beams.visible) {
      const bp = beamGeo.attributes.position.array as Float32Array;
      for (let b = 0; b < beamCount; b++) {
        const a = beamPairs[b * 2] * 3;
        const c = (broken[b] ? beamPairs[b * 2] : beamPairs[b * 2 + 1]) * 3;
        bp[b * 6] = pos[a];
        bp[b * 6 + 1] = pos[a + 1];
        bp[b * 6 + 2] = pos[a + 2];
        bp[b * 6 + 3] = pos[c];
        bp[b * 6 + 4] = pos[c + 1];
        bp[b * 6 + 5] = pos[c + 2];
      }
      beamGeo.attributes.position.needsUpdate = true;
    }
    if (nodes.visible) {
      for (let i = 0; i < nodeCount; i++) {
        m4.makeTranslation(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);
        nodes.setMatrixAt(i, m4);
      }
      nodes.instanceMatrix.needsUpdate = true;
    }
  }

  function render(): void {
    controls.update();
    renderer.render(scene, camera);
  }

  function resize(): void {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w < 2 || h < 2) return;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  function setEco(v: boolean): void {
    eco = v;
    renderer.setPixelRatio(eco ? 1 : Math.min(2, window.devicePixelRatio || 1));
    renderer.shadowMap.enabled = !eco;
    sun.castShadow = !eco;
    resize();
  }

  function info(): SceneInfo {
    const r = renderer.info as unknown as { render?: { drawCalls?: number; calls?: number; triangles?: number }; memory?: { geometries?: number; textures?: number } };
    return {
      backend,
      calls: r.render?.drawCalls ?? r.render?.calls ?? 0,
      triangles: r.render?.triangles ?? 0,
      geometries: r.memory?.geometries ?? 0,
      textures: r.memory?.textures ?? 0,
      gpuBytes
    };
  }

  return {
    update,
    render,
    resize,
    setBeamsVisible: (v) => {
      beams.visible = v;
    },
    setNodesVisible: (v) => {
      nodes.visible = v;
    },
    setEco,
    info
  };
}
